const interFontFamily = '"Inter", Roboto, "Helvetica Neue", Arial, sans-serif';

// Font weight constants matching design system
export const fontWeights = {
  medium: 500,
  semiBold: 600,
  bold: 700,
} as const;

export const typography = {
  fontFamily: interFontFamily,
  // Display variants (largest text sizes)
  displayLarge: {
    fontSize: '3.5625rem', // 57px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.03em', // 3%
  },
  displayMedium: {
    fontSize: '2.8125rem', // 45px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  displaySmall: {
    fontSize: '2.25rem', // 36px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  // Heading variants
  h1: {
    fontSize: '2rem', // 32px
    fontWeight: fontWeights.semiBold,
    lineHeight: 'normal',
    letterSpacing: '0.03em', // 3%
  },
  h2: {
    fontSize: '1.5rem', // 24px
    fontWeight: fontWeights.semiBold,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  h3: {
    fontSize: '1.125rem', // 18px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.01em', // 1%
  },
  h4: {
    fontSize: '1.5rem', // 24px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  h5: {
    fontSize: '1.25rem', // 20px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  h6: {
    fontSize: '1rem', // 16px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  // Subtitle variants
  subtitle1: {
    fontSize: '1rem', // 16px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  subtitle2: {
    fontSize: '0.875rem', // 14px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  // Body variants
  body1: {
    fontSize: '1rem', // 16px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  body2: {
    fontSize: '0.875rem', // 14px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  // Caption variants
  caption1: {
    fontSize: '0.75rem', // 12px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  caption2: {
    fontSize: '0.6875rem', // 11px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
  },
  // Overline variant
  overline: {
    fontSize: '0.625rem', // 10px
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em', // 2%
    textTransform: 'uppercase' as const,
  },
  button: {
    fontWeight: fontWeights.semiBold,
    textTransform: 'capitalize' as const,
    whiteSpace: 'nowrap' as const,
  },
  // Custom variants for heavier font weights per design system
  // DisplayLarge variants (57px) - default is medium
  displayLargeBold: {
    fontSize: '3.5625rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.03em',
  },
  // DisplayMedium variants (45px) - default is medium
  displayMediumBold: {
    fontSize: '2.8125rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // DisplaySmall variants (36px) - default is medium
  displaySmallBold: {
    fontSize: '2.25rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // H1 variants (32px) - default is semiBold
  h1Bold: {
    fontSize: '2rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.03em',
  },
  // H2 variants (24px) - default is semiBold
  h2Bold: {
    fontSize: '1.5rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // H3 variants (18px) - default is medium
  h3SemiBold: {
    fontSize: '1.125rem',
    fontWeight: fontWeights.semiBold,
    lineHeight: 'normal',
    letterSpacing: '0.01em',
  },
  h3Bold: {
    fontSize: '1.125rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.01em',
  },
  // Subtitle1 variants (16px) - default is medium
  subtitle1SemiBold: {
    fontSize: '1rem',
    fontWeight: fontWeights.semiBold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  subtitle1Bold: {
    fontSize: '1rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // Subtitle2 variants (14px) - default is medium
  subtitle2SemiBold: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.semiBold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  subtitle2Bold: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // Body1 variants (16px) - default is medium
  body1SemiBold: {
    fontSize: '1rem',
    fontWeight: fontWeights.semiBold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  body1Bold: {
    fontSize: '1rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // Body2 variants (14px) - default is medium
  body2SemiBold: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.semiBold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  body2Bold: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // Caption1 variants (12px) - default is medium
  caption1Bold: {
    fontSize: '0.75rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  caption1Italic: {
    fontSize: '0.75rem',
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    fontStyle: 'italic' as const,
  },
  // Caption2 variants (11px) - default is medium
  caption2Bold: {
    fontSize: '0.6875rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
  },
  // Body1 underline and italic variants (16px)
  body1Underline: {
    fontSize: '1rem',
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    textDecoration: 'underline' as const,
  },
  body1BoldUnderline: {
    fontSize: '1rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    textDecoration: 'underline' as const,
  },
  body1Italic: {
    fontSize: '1rem',
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    fontStyle: 'italic' as const,
  },
  // Body2 underline and italic variants (14px)
  body2Underline: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    textDecoration: 'underline' as const,
  },
  body2BoldUnderline: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.bold,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    textDecoration: 'underline' as const,
  },
  body2Italic: {
    fontSize: '0.875rem',
    fontWeight: fontWeights.medium,
    lineHeight: 'normal',
    letterSpacing: '0.02em',
    fontStyle: 'italic' as const,
  },
  // Error variant for displaying error messages
  bodyError: {
    color: '#b00020',
    lineHeight: 1.5,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  },
};

// TypeScript declarations for custom typography variants
declare module '@mui/material/styles' {
  interface TypographyVariants {
    displayLarge: React.CSSProperties;
    displayLargeBold: React.CSSProperties;
    displayMedium: React.CSSProperties;
    displayMediumBold: React.CSSProperties;
    displaySmall: React.CSSProperties;
    displaySmallBold: React.CSSProperties;
    h1Bold: React.CSSProperties;
    h2Bold: React.CSSProperties;
    h3SemiBold: React.CSSProperties;
    h3Bold: React.CSSProperties;
    subtitle1SemiBold: React.CSSProperties;
    subtitle1Bold: React.CSSProperties;
    subtitle2SemiBold: React.CSSProperties;
    subtitle2Bold: React.CSSProperties;
    body1SemiBold: React.CSSProperties;
    body1Bold: React.CSSProperties;
    body1Underline: React.CSSProperties;
    body1BoldUnderline: React.CSSProperties;
    body1Italic: React.CSSProperties;
    body2SemiBold: React.CSSProperties;
    body2Bold: React.CSSProperties;
    body2Underline: React.CSSProperties;
    body2BoldUnderline: React.CSSProperties;
    body2Italic: React.CSSProperties;
    caption1: React.CSSProperties;
    caption1Bold: React.CSSProperties;
    caption1Italic: React.CSSProperties;
    caption2: React.CSSProperties;
    caption2Bold: React.CSSProperties;
    bodyError: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    displayLarge?: React.CSSProperties;
    displayLargeBold?: React.CSSProperties;
    displayMedium?: React.CSSProperties;
    displayMediumBold?: React.CSSProperties;
    displaySmall?: React.CSSProperties;
    displaySmallBold?: React.CSSProperties;
    h1Bold?: React.CSSProperties;
    h2Bold?: React.CSSProperties;
    h3SemiBold?: React.CSSProperties;
    h3Bold?: React.CSSProperties;
    subtitle1SemiBold?: React.CSSProperties;
    subtitle1Bold?: React.CSSProperties;
    subtitle2SemiBold?: React.CSSProperties;
    subtitle2Bold?: React.CSSProperties;
    body1SemiBold?: React.CSSProperties;
    body1Bold?: React.CSSProperties;
    body1Underline?: React.CSSProperties;
    body1BoldUnderline?: React.CSSProperties;
    body1Italic?: React.CSSProperties;
    body2SemiBold?: React.CSSProperties;
    body2Bold?: React.CSSProperties;
    body2Underline?: React.CSSProperties;
    body2BoldUnderline?: React.CSSProperties;
    body2Italic?: React.CSSProperties;
    caption1?: React.CSSProperties;
    caption1Bold?: React.CSSProperties;
    caption1Italic?: React.CSSProperties;
    caption2?: React.CSSProperties;
    caption2Bold?: React.CSSProperties;
    bodyError?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    // Enable custom variants
    // NOTE: MUI's default `caption` variant is intentionally left enabled here
    // (unlike Luci.Shopping.UI, which disables it) because DevelopmentInfo still
    // uses `variant="caption"`.
    displayLarge: true;
    displayLargeBold: true;
    displayMedium: true;
    displayMediumBold: true;
    displaySmall: true;
    displaySmallBold: true;
    h1Bold: true;
    h2Bold: true;
    h3SemiBold: true;
    h3Bold: true;
    subtitle1SemiBold: true;
    subtitle1Bold: true;
    subtitle2SemiBold: true;
    subtitle2Bold: true;
    body1SemiBold: true;
    body1Bold: true;
    body1Underline: true;
    body1BoldUnderline: true;
    body1Italic: true;
    body2SemiBold: true;
    body2Bold: true;
    body2Underline: true;
    body2BoldUnderline: true;
    body2Italic: true;
    caption1: true;
    caption1Bold: true;
    caption1Italic: true;
    caption2: true;
    caption2Bold: true;
    bodyError: true;
  }
}

export { interFontFamily };
